import { IEgressEventMappingModel, IExceptionModel, IIngressEventMappingModel, IIngressEventValidationModel, IIngressExceptionModel } from '../../models/index';
import { IExceptionState, ISearchListState } from '../../modules/services/service-wrapper/service-operation/event-association/service-event-state-manager/ServiceEventStateManager';
import { IActiveServiceEvent } from '../../store/actions/types/services/GetAllServiceEventTypes';

/**
 * @method getAssociatedIngressEvents
  * @description Returns the associated ingress event for service operation
  * @returns ingress event list
  */
export const getAssociatedIngressEvents = (ingressEventMappingList: IIngressEventMappingModel[], serviceOperationName: string) => {
  const eventList = ingressEventMappingList.filter(
    (event) => event.serviceOperationName === serviceOperationName
  );
  return eventList;
};

/**
 * @method getAssociatedEgressEvents
  * @description Returns the associated egress events for service operation
  * @returns egress event list
  */
export const getAssociatedEgressEvents = (egressEventMappingList: IEgressEventMappingModel[], serviceOperationName: string) => {
  const eventList = egressEventMappingList.filter(
    (event) => event.serviceOperationName === serviceOperationName
  );
  return eventList;
};

/**
 * @method getAssociatedIngressEgressEvents
  * @description Returns the associated egress events for service operation
  * @returns egress event list
  */
export const getAssociatedIngressEgressEvents = (egressEventMappingList: IEgressEventMappingModel[], serviceOperationName: string, ingressName: string, ingressEventVersion: string) => {
  const eventList = egressEventMappingList.filter(
    (event) => event.serviceOperationName === serviceOperationName && event.ingressName === ingressName && event.ingressEventVersion === ingressEventVersion
  );
  return eventList;
};

/**
 * @method isEgressEventExist
 * @description Checks for egress event exist
 * @param egressEventMappingList : ingress event list
 * @param egressEventName: Event to be check
 * @returns true if exist else false
 */
export const isEgressEventExist = (egressEventMappingList: IEgressEventMappingModel[], egressEventName: string, serviceOperationName: string, version: string, ingressEventVersion: string, ingressName?: string) => egressEventMappingList.some((egressEvent) => egressEvent.eventName === egressEventName && egressEvent.serviceOperationName === serviceOperationName && egressEvent.ingressName === ingressName && egressEvent.ingressEventVersion === ingressEventVersion && egressEvent.eventVersion === version);

/**
 * @method getActionsForException
 * @description Gets the actions associated with the selected exception
 * @param exceptionList : ExceptionList
 * @param exceptionId : Exception Id
 * @returns actions array
 */
export const getActionsForException = (exceptionList: IIngressExceptionModel[], exceptionId: string) => Array.from(exceptionList.filter((exception) => exception.exceptionId === exceptionId).map((exception) => exception.action).flat());

/**
 * @method isIngressEventDetailsUpdated
 *  @description check for ingress event details updated or not
 */
export const isIngressEventDetailsUpdated = (
  ingressEventMappingList: IIngressEventMappingModel[],
  serviceOperationName: string,
  currentIngressEventModel: IIngressEventMappingModel
) => {
  const event = getAssociatedIngressEvents(ingressEventMappingList, serviceOperationName);
  return JSON.stringify(event[0]) === JSON.stringify(currentIngressEventModel);
};

/**
 * @method constructIngressEventObject
 * @description Construct Ingress Event object
 * @param ingressEventformData: Ingress event data
 * @param serviceOperationName: service operation name
 * @param searchListState: serarch list state
 * @param exceptinState: exceptions state
 * @returns Ingress event object
 */
export const constructIngressEventObject = (
  ingressEventformData: IIngressEventValidationModel,
  serviceOperationName: string,
  searchListState: ISearchListState,
  exceptinState: IExceptionState
) => {
  let currentFormIngressEvent: IIngressEventMappingModel = {
    serviceOperationName,
    eventName: '',
    eventVersion: '',
    reqAttribute: '',
    payloadToQueryPath: '',
    payloadToRequestHeader: '',
    headerToRequestHeader: '',
    headerToRequestPayload: '',
    requestToAuthenticationPayload: '',
    exceptions: []
  };
  if (searchListState.searchListData.length) {
    const searchedEvent = searchListState.searchListData[0];
    currentFormIngressEvent = {
      serviceOperationName,
      eventName: searchedEvent.eventName,
      eventVersion: String(searchedEvent.version),
      reqAttribute: ingressEventformData.requestAttribute.value,
      payloadToQueryPath: ingressEventformData.payloadToQueryPath.value,
      payloadToRequestHeader: ingressEventformData.payloadToRequestHeader.value,
      headerToRequestHeader: ingressEventformData.headerToRequestHeader.value,
      headerToRequestPayload: ingressEventformData.headerToRequestPayload.value,
      requestToAuthenticationPayload: ingressEventformData.requestToAuthenticationPayload.value,
      exceptions: exceptinState.exceptionList
    };
  }
  return currentFormIngressEvent;
};

/**
 * @method isEgressErrorCondition
 * @description Checks for the Error condition is there or not for egress event association mapping
 * @param searchListDataLength: length of serched event
 * @param egressEventMappingList: Egress event mapping list
 * @param eventName: event Name
 * @param serviceOperationName: service operation name
 * @returns: true if condition met
 */
export const isEgressErrorCondition = (
  searchListDataLength: number,
  egressEventMappingList: IEgressEventMappingModel[],
  eventName: string,
  serviceOperationName: string,
  ingressName: string,
  ingressEventVersion: string,
  version: string
) => searchListDataLength > 0 && isEgressEventExist(egressEventMappingList, eventName, serviceOperationName, version, ingressEventVersion, ingressName);

/**
 * @method isErrorCondition
 * @description Checks for the Error condition is there or not for service event association mapping
 * @param searchListDataLength: length of searched event
 * @param ingressEventMappingList: Ingress event mapping list
 * @param egressEventMappingList: Egress event mapping list
 * @param eventName: event Name
 * @param serviceOperationName: service operation name
 * @returns: true if condition met
 */
export const isErrorCondition = (
  searchListDataLength: number,
  egressEventMappingList: IEgressEventMappingModel[],
  eventName: string,
  serviceOperationName: string,
  ingressName: string,
  version: string,
  ingressEventVersion: string
) => searchListDataLength > 0 && ((eventName === ingressName && version === ingressEventVersion) || isEgressEventExist(egressEventMappingList, eventName, serviceOperationName, version, ingressEventVersion, ingressName));

/**
 * @method getErrorCodeForSelectedException
   * @description Gets the error code for selected exception
   */
export const getErrorCodeForSelectedException = (selectedException: string, exceptions: IExceptionModel[]) => {
  let errorCode = [];
  errorCode = exceptions.filter(
    (item) => item.exceptionId === selectedException
  );
  return errorCode.length ? errorCode[0].errorCode : '';
};

/**
 * @method getExceptionActions
 * @description Gets the exception actions
 * @param selectedException: current selected exception
 * @param exceptions: exception list
 * @returns: actions array
 */
export const getExceptionActions = (selectedException: string, exceptions: IExceptionModel[]) => {
  const actionArray = exceptions
    .filter(
      (exception) =>
        exception.exceptionId === selectedException
        && exception.errorCode === getErrorCodeForSelectedException(selectedException, exceptions)
    )
    .map((exception) => exception.action);
  return Array.from(new Set(actionArray.flat()));
};

/**
 * @method getAssociatedActionsForSelectedException
 * @description Gets the associated actions for selected exceptions
 * @param selectedException: current selected exception
 * @param exceptions: exception list
 * @returns actions
 */
export const getAssociatedActionsForSelectedException = (selectedException: string, exceptions: IExceptionModel[]) => {
  const actionArray = exceptions
    .filter(
      (exception) =>
        exception.exceptionId === selectedException
        && exception.errorCode === getErrorCodeForSelectedException(selectedException, exceptions)
    )
    .map((exception) => exception.action);
  const mergedActionArray = Array.from(new Set(actionArray.flat()));
  const dropDownValues = mergedActionArray.map((action) => {
    const actionObject = { label: action, value: action };
    return actionObject;
  });
  return dropDownValues;
};

/**
 * @method getExceptionName
 * @description Gets the exception name for exception dropdown field
 * @returns exception name array
 */
export const getExceptionName = (exceptions: IExceptionModel[]): { value: number; name: string }[] => {
  const exceptionNameArray: { value: number; name: string }[] = [];
  exceptions.forEach((exception, index) => {
    const exceptionObject = {
      name: exception.exceptionId,
      value: index
    };
    exceptionNameArray.push(exceptionObject);
  });
  return exceptionNameArray;
};

/**
 * @method checkForExceptionExist
 * @description Check Exception exist in current list or not
 * @param selectedException: current selected exception
 * @param exceptions: exception list
 * @param action: actions array
 * @param formState: form state
 * @returns true if exist else false
 */
export const checkForExceptionExist = (selectedException: string, exceptions: IExceptionModel[], action: string[], formState: IExceptionState): boolean => {
  if (exceptions.length <= 0 || selectedException === '' || !action.length) {
    return true;
  }

  const actionsArray = getActionsForException(
    formState.exceptionList,
    selectedException
  );
  const newException: IIngressExceptionModel = {
    exceptionId: selectedException,
    errorCode: `${getErrorCodeForSelectedException(selectedException, exceptions)}`,
    action: formState.isEditExceptionClicked
      ? action
      : Array.from(new Set([...actionsArray, ...action]))
  };
  let exceptionExist = false;
  formState.exceptionList.forEach((exception) => {
    if (JSON.stringify(exception) === JSON.stringify(newException)) {
      exceptionExist = true;
    }
  });
  return exceptionExist;
};

/**
 * @method isJson
 * @description Checks data is valid json or not
 * @param data: to be verify
 * @returns true if valid json else false
 */
export const isJson = (data: string): boolean => {
  if (typeof data !== 'string') return false;
  try {
    const result = JSON.parse(data); return typeof result === 'object' && result !== null;
} catch (err) {
return false;
}
};

/**
 * @method checkEgressForEditMode
 * @param isEditButtonClick : boolean
 * @param viewMode : boolean
 * @returns true if edit mode, else false
 */
export const checkEgressForEditMode = (isEditButtonClick: boolean, viewMode: boolean | undefined) => !isEditButtonClick && !viewMode;

/**
 * @method egressErrorMessageHandler
 * @param messageData : object
 * @returns the proper error message for Egress events
 */
export const egressErrorMessageHandler = (messageData: { notFoundError: boolean, notFoundErrorMessage: string, duplicateEgressErrorMsg: string, isIngressExist: boolean, duplicateIngressErrorMsg: string }) => {
  const { notFoundError, notFoundErrorMessage, isIngressExist, duplicateIngressErrorMsg, duplicateEgressErrorMsg } = messageData;
  if (notFoundError) {
    return notFoundErrorMessage;
  }
  if (isIngressExist) {
    return duplicateIngressErrorMsg;
  }
  return duplicateEgressErrorMsg;
};

/**
 * @method isIngressExistForEgress
 * @param event: current active event
 * @param ingressName: ingress event name
 * @param ingressVersion: ingress event version
 * @returns  true if error, else false
 */
export const isIngressExistForEgress = (event: IActiveServiceEvent, ingressName: string, ingressVersion: string) => event?.eventName === ingressName && event?.version?.toString() === ingressVersion;

/**
 * @method isErrorIngressCondition
 * @param isSearchDataNotEmpty: boolean
 * @param isIngressExist: boolean
 * @returns true if ingress error condition
 */
export const isErrorIngressCondition = (isSearchDataNotEmpty: boolean, isIngressExist: boolean) => isSearchDataNotEmpty && isIngressExist;

/**
 * @method isEgressEventError
 * @param notFoundError: not found error
 * @param errorIngressCondition: ingress event error condition
 * @returns true if egress event error, else false
 */
export const isEgressEventError = (notFoundError: boolean, errorIngressCondition: boolean) => !notFoundError && errorIngressCondition;
